import * as THREE from 'three';
import { msg } from '../../../utils/game/logMessage';

// Scry session orchestration, extracted from GameBoard.jsx.
//
// Every function takes the GameBoard instance as `host` and reads/writes
// the `scry` slice of host.state. The session model: `peeked[]` is every
// card shown so far in peek order, `decisions` maps cardId -> destination
// ('hand' | 'opposite' | 'cemetery' | 'same'; keep is implicit), and the
// pile itself stays untouched until the session finalizes.
//
// Host contract: props.isSpectating, state.gameState/scry, setState,
// scene (setPaused/camera), canvasRef, pileMeshes, findOrCreateCemetery,
// addToHand, updatePileMeshes, logAction.

// Aggregate-log formatter for a completed scry session. Card names
// are deliberately excluded. Scrying is private info, and the
// opponent's log gets the same shape as the local log so the
// broadcast stays symmetrical and never leaks identities.
const pileEndLabel = (end) => (end === 'top' ? msg('top') : msg('bottom'));

export function buildScrySummaryText(summary) {
  const { pileName = msg('pile'), from = 'top', counts = {} } = summary || {};
  const { hand = 0, cemetery = 0, opposite = 0, keep = 0, same = 0 } = counts;
  const total = hand + cemetery + opposite + keep + same;
  if (total === 0) return msg('Scried {pile}', { pile: pileName });
  const parts = [];
  if (hand > 0) parts.push(msg('{n} to hand', { n: hand }));
  if (cemetery > 0) parts.push(msg('{n} to cemetery', { n: cemetery }));
  if (opposite > 0) parts.push(msg('{n} to {end}', { n: opposite, end: pileEndLabel(from === 'top' ? 'bottom' : 'top') }));
  if (same > 0) parts.push(msg('{n} reordered on {end}', { n: same, end: pileEndLabel(from) }));
  if (keep > 0) parts.push(msg('{n} kept on {end}', { n: keep, end: pileEndLabel(from) }));
  // Joined pairwise so the separator itself is a translatable key.
  const details = parts.reduce((acc, part) => msg('{a}, {b}', { a: acc, b: part }));
  const values = { n: total, end: pileEndLabel(from), pile: pileName, details };
  return total === 1
    ? msg('Scried 1 card from {end} of {pile}. {details}', values)
    : msg('Scried {n} cards from {end} of {pile}. {details}', values);
}

// Project a pile's world position into screen-pixel coords so the
// scry dialog can anchor itself near the actual pile on the board.
// Falls back to pile.x/pile.z when the mesh doesn't exist yet (e.g.
// a cemetery with no cards in it).
export function computePileAnchor(host, pile) {
  const canvas = host.canvasRef?.current;
  if (!pile || !canvas || !host.scene?.camera) return null;
  const pileMesh = host.pileMeshes?.get(pile.id);
  const pos = pileMesh
    ? pileMesh.position.clone()
    : new THREE.Vector3(pile.x || 0, 0, pile.z || 0);
  pos.y += 2;
  pos.project(host.scene.camera);
  const x = ((pos.x + 1) / 2) * canvas.clientWidth;
  const y = ((-pos.y + 1) / 2) * canvas.clientHeight;
  return { x, y };
}

export function openScry(host, pile, from, opts = {}) {
  if (host.props.isSpectating) return;
  if (!pile || pile.cards.length === 0) {
    host.setState({ contextMenu: null });
    return;
  }
  const idx = from === 'top' ? pile.cards.length - 1 : 0;
  const current = pile.cards[idx];
  const pileAnchor = computePileAnchor(host, pile);
  const cemetery = host.findOrCreateCemetery(current.rotated);
  const cemeteryAnchor = computePileAnchor(host, cemetery);
  // v1.64. Per-effect destination gating + opponent-pile support.
  // `allowedDestinations` is an optional iterable of 'hand' / 'same'
  // / 'opposite' / 'cemetery'. When provided, ScryDialog disables
  // buttons not in the set. Used by card-text-driven scry where
  // the codex restricts where peeked cards can go (e.g. "look at
  // the top 3 cards of your spellbook, put each on the top or
  // bottom in any order"). Default (null) keeps all four enabled.
  // `maxPeek` caps the "Peek next" button so the player can't scry
  // past the codex limit. Default = pile size (current behaviour).
  const allowedDestinations = opts.allowedDestinations
    ? new Set(opts.allowedDestinations)
    : null;
  const maxPeek = typeof opts.maxPeek === 'number' && opts.maxPeek > 0
    ? Math.min(opts.maxPeek, pile.cards.length)
    : pile.cards.length;
  host.scene?.setPaused(true);
  host.setState({
    contextMenu: null,
    scry: {
      pileId: pile.id,
      pileName: pile.name,
      from,
      peeked: [current],
      decisions: {},
      // sameOrder records the click order of 'same' assignments so
      // the user can stack multiple cards back onto the scried end
      // in a chosen order. First-clicked = next-to-draw.
      sameOrder: [],
      activeIndex: 0,
      remainingBelow: pile.cards.length - 1,
      anchor: pileAnchor,
      cemeteryAnchor,
      allowedDestinations,
      maxPeek,
      // Cradle of Etherrum: when the peek source may also CAST the
      // top card, the dialog shows a Summon button alongside "leave
      // it." { sourceCardId, controller, filter, cost_delta, need }
      castOption: opts.castOption || null,
    },
  });
  // A follow-up to run once the session closes by ANY exit (finalize,
  // keep-and-done, cancel). Riddle Sphinx draws after its peek resolves.
  // Stored off-state (not JSON-serializable) since it is a closure that
  // only matters within the live session.
  host._scryOnComplete = typeof opts.onComplete === 'function' ? opts.onComplete : null;
}

// Reveal another card from the pile into the peeked pool and make
// it active. Returns false when the pile has been fully peeked.
export function scryAdvance(host, decisions, activeIndex) {
  const scry = host.state.scry;
  if (!scry) return false;
  const pile = host.state.gameState.piles.find((p) => p.id === scry.pileId);
  if (!pile) return false;
  // v1.64. Honor maxPeek so card-text-driven scry can cap reveals
  // ("Scry 2" must stop after the second peek).
  if (typeof scry.maxPeek === 'number' && scry.peeked.length >= scry.maxPeek) return false;
  const peekedSet = new Set(scry.peeked.map((c) => c.id));
  let next = null;
  if (scry.from === 'top') {
    for (let i = pile.cards.length - 1; i >= 0; i--) {
      if (!peekedSet.has(pile.cards[i].id)) { next = pile.cards[i]; break; }
    }
  } else {
    for (let i = 0; i < pile.cards.length; i++) {
      if (!peekedSet.has(pile.cards[i].id)) { next = pile.cards[i]; break; }
    }
  }
  if (!next) return false;
  const peeked = [...scry.peeked, next];
  host.setState({
    scry: {
      ...scry,
      peeked,
      decisions,
      activeIndex: activeIndex != null ? activeIndex : peeked.length - 1,
      remainingBelow: pile.cards.length - peeked.length,
    },
  });
  return true;
}

// Apply every accumulated decision to the pile in one pass. Cards
// without an explicit decision are treated as "keep" and stay put.
// `sameOrder` lists card ids the user assigned to 'same' (back to
// the scried end), in click order. First-clicked = next-to-draw.
export function scryFinalize(host, decisions, sameOrder = []) {
  const scry = host.state.scry;
  if (!scry) return;
  const pile = host.state.gameState.piles.find((p) => p.id === scry.pileId);
  if (!pile) { host.scene?.setPaused(false); host.setState({ scry: null }); runScryOnComplete(host); return; }

  const counts = { hand: 0, cemetery: 0, opposite: 0, keep: 0, same: 0 };
  let touchedCemetery = null;
  for (const card of scry.peeked) {
    const dest = decisions[card.id];
    if (!dest || dest === 'keep') {
      counts.keep++;
      continue;
    }
    const cardIdx = pile.cards.indexOf(card);
    if (cardIdx !== -1) pile.cards.splice(cardIdx, 1);
    if (dest === 'hand') {
      host.addToHand(card);
      host._emitMoveToZone?.(card.id, 'hand');
      counts.hand++;
    } else if (dest === 'cemetery') {
      const cemetery = host.findOrCreateCemetery(card.rotated);
      cemetery.cards.push(card);
      host._emitMoveToZone?.(card.id, 'cemetery');
      touchedCemetery = cemetery;
      counts.cemetery++;
    } else if (dest === 'opposite') {
      if (scry.from === 'top') pile.cards.unshift(card);
      else pile.cards.push(card);
      counts.opposite++;
    } else if (dest === 'same') {
      // Re-added below in user-chosen order. Only count here.
      counts.same++;
    }
  }
  // Re-stack 'same' decisions onto the scried end in click order.
  // Each click physically lays that card on top of the prior one,
  // so the LAST card the user clicked ends up at the visible end
  // of the pile (next to draw). Same mental model as actually
  // stacking cards on a real table.
  if (sameOrder.length > 0) {
    for (const id of sameOrder) {
      if (decisions[id] !== 'same') continue;
      const card = scry.peeked.find((c) => c.id === id);
      if (!card) continue;
      if (scry.from === 'top') pile.cards.push(card);
      else pile.cards.unshift(card);
    }
  }
  host.updatePileMeshes();

  // The arrangement is the whole point of a scry, so it has to reach the
  // server. Everything above only mutated `pile.cards` locally; the mirror kept
  // the pre-scry order, and the render reconciler put that order straight back
  // on the next diff. The card scried to the bottom became the next draw,
  // permanently, because nothing ever emitted (valkenhall-qa#53).
  //
  // Cards routed to hand/cemetery emitted their own MOVE_TO_ZONE above
  // (valkenhall-qa#163: without it the server pile kept them, this reorder
  // failed the permutation gate for missing their ids, and the next mirror
  // fold put them back on top of the spellbook). Those moves are already on
  // the wire, so the server's pile matches this order when it validates.
  host._emitPileReorder?.(pile);

  // Counts only, never card identities. Scrying is private info.
  const summary = { pileName: pile.name, from: scry.from, counts };
  host.logAction(buildScrySummaryText(summary));

  host.scene?.setPaused(false);
  host.setState({ scry: null });
  runScryOnComplete(host);
}

// Run and clear the one-shot session callback (Riddle Sphinx's draw).
// Guarded so a session without a callback is a no-op, and so the
// callback can never fire twice.
function runScryOnComplete(host) {
  const cb = host._scryOnComplete;
  host._scryOnComplete = null;
  if (cb) cb();
}

// Record a decision for the currently-active card. If every peeked
// card now has a decision, finalize immediately. The session is
// over. Otherwise, auto-advance to the next undecided thumbnail so
// the user can keep assigning quickly.
export function scryAssignCurrent(host, destination) {
  const scry = host.state.scry;
  if (!scry) return;
  const current = scry.peeked[scry.activeIndex];
  if (!current) return;
  const decisions = { ...scry.decisions, [current.id]: destination };
  // Track click order for 'same' decisions so the user's chosen
  // stacking order survives into scryFinalize. Switching the same
  // card to a different destination removes it from the order.
  const prevOrder = scry.sameOrder || [];
  const sameOrder = destination === 'same'
    ? (prevOrder.includes(current.id) ? prevOrder : [...prevOrder, current.id])
    : prevOrder.filter((id) => id !== current.id);
  const allDecided = scry.peeked.every((c) => decisions[c.id]);
  if (allDecided) {
    scryFinalize(host, decisions, sameOrder);
    return;
  }
  let nextIdx = scry.activeIndex;
  const n = scry.peeked.length;
  for (let i = 1; i <= n; i++) {
    const j = (scry.activeIndex + i) % n;
    if (!decisions[scry.peeked[j].id]) { nextIdx = j; break; }
  }
  host.setState({ scry: { ...scry, decisions, sameOrder, activeIndex: nextIdx } });
}

// "Peek next." Add another card from the pile to the pool and
// make it active. Does NOT commit any decision for the previous
// card; kept cards are those without a recorded destination.
export function scryPeekNext(host) {
  const scry = host.state.scry;
  if (!scry) return;
  scryAdvance(host, scry.decisions);
}

// Change which peeked card is shown in the large view. User can
// navigate freely among already-peeked cards.
export function scrySetActive(host, idx) {
  const scry = host.state.scry;
  if (!scry) return;
  if (idx < 0 || idx >= scry.peeked.length || idx === scry.activeIndex) return;
  host.setState({ scry: { ...scry, activeIndex: idx } });
}

// "Done." Stop early. Undecided cards stay where they are.
export function scryKeepAndDone(host) {
  const scry = host.state.scry;
  if (!scry) { host.scene?.setPaused(false); host.setState({ scry: null }); runScryOnComplete(host); return; }
  scryFinalize(host, scry.decisions, scry.sameOrder || []);
}

export function scryCancel(host) {
  // Peeking was non-destructive; no pile mutation to undo.
  host.scene?.setPaused(false);
  host.setState({ scry: null });
  runScryOnComplete(host);
}

// Cradle of Etherrum's "Summon" from the peek dialog. Closes the
// session (the peek itself was non-destructive) and routes the cast
// through the standard cast-from-top resolver. Payment, pile pop,
// and board spawn all live there. The source may be field-placed
// (own mesh) or an equipped Relic (lives in a bearer's equipment
// array), so the lookup covers both.
export function scryCastFromTop(host) {
  const cast = host.state.scry?.castOption;
  host.scene?.setPaused(false);
  host.setState({ scry: null }, () => {
    if (!cast) return;
    let source = host._findCardById(cast.sourceCardId);
    if (!source) {
      for (const [, mesh] of host.meshes) {
        const c = mesh?.userData?.cardInstance;
        const eq = c?.equippedCards?.find((e) => e.id === cast.sourceCardId);
        if (eq) { source = eq; break; }
      }
    }
    if (!source) return;
    host._resolveCastTopOfSpellbook(source, {
      effect: [{
        op: 'cast_top_of_spellbook',
        args: { filter: cast.filter, cost_delta: cast.cost_delta },
      }],
    }, cast.controller);
  });
}
